<template>
  <div
    id="flashcard-view-container-v2"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <template v-if="answered">
      <answered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :correct="correct"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
      />
      <answered-footer
        :show-flashcard-statuses="true"
        @tag="onTag"
      />
    </template>
    <template v-else>
      <unanswered-header
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
      />
      <unanswered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
        :terminology="isTerminology"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
        @try-answer-flashcard="onAnswered"
      />
      <unanswered-footer
        :can-submit-answer="canSubmitAnswer"
        :flashcard="currentFlashcard"
        @answer="onAnswered"
        @next-flashcard="goToNextFlashcard"
      />
    </template>
  </div>
</template>

<script>
import AnsweredFlashcard from '@components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import UnansweredFlashcard from '@components/flashcards-v2/UnansweredFlashcard';
import UnansweredFooter from '@components/flashcards-v2/UnansweredFooter';
import UnansweredHeader from '@components/flashcards-v2/UnansweredHeader.vue';

import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';
import RateLimitMixin from '@/mixins/rate-limit';
import { numSelectionsSatisfied } from '@/utils/flashcards';

export default {
  name: 'DeeplinkFlashcardV2View',
  components: {
    AnsweredFlashcard,
    AnsweredFooter,
    UnansweredFlashcard,
    UnansweredFooter,
    UnansweredHeader,
  },
  mixins: [FlashcardViewMixin, GlobalMixin, QuestionsUIV2Mixin, RateLimitMixin],
  computed: {
    canSubmitAnswer() {
      return this.hasBeenAnswered && numSelectionsSatisfied(this.currentFlashcard, this.answerIds);
    },
    currentFlashcardCategory() {
      return this.primaryFlashcardCategory(this.currentFlashcard);
    },
    nextFlashcardRoute() {
      return { name: 'home' };
    },
    shouldShowRateLimitModal() {
      if (this.isQotD) return false;
      if (!this.flashcardIsRateLimited(this.currentFlashcard, this.currentFlashcardCategory)) return false;
      if (!this.creditLimitReached) return false;
      return true;
    },
  },
  watch: {
    'currentFlashcard.type'(type) {
      if (type) this.maybeRedirectToCorrectVersion(type);
    },
  },
  created() {
    this.maybeRedirectToCorrectVersion(this.currentFlashcard?.type);
    this.$store.dispatch('flashcards/fetchFlashcards', { ids: [this.$route.params.flashcardId], rateLimited: true });
  },
  mounted() {
    const discussionId = this.$route.query.discussionId;
    // catch so a rejection can't go unhandled or throw past the rate-limit gating below
    const statePromise = this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards', 'stats']).catch(() => null);
    const discussionsPromise = discussionId
      ? this.$store.dispatch('discussions/fetchDiscussions', { flashcardId: this.$route.params.flashcardId }).catch(() => null)
      : null;

    this.$watch('currentFlashcard.id', async (newId, oldId) => {
      if (!newId || newId === oldId) return;

      if (discussionId && discussionsPromise) {
        await Promise.all([statePromise, discussionsPromise]);
        const discussions = this.$store.getters['discussions/getDiscussionsByFlashcardId'](newId) || [];
        const discussionIsReal = discussions.some(d => String(d.id) === String(discussionId));
        if (discussionIsReal) {
          this.restoreStateFromFlashcardState(this.currentFlashcard);
          // showAnswer opens the answer side and comments for an unanswered question, unless rate limited
          if (this.$route.query.showAnswer === 'true' && !this.answered && !this.shouldShowRateLimitModal) {
            this.answered = true;
          }
        }
      }

      if (!this.answered && this.shouldShowRateLimitModal) {
        // modal's beforeOpen bails on a falsy category; fall back so it never opens blank
        this.$modal.show('rate-limit-reached', {
          category: this.currentFlashcardCategory ?? { id: this.currentFlashcard?.categoryIds?.[0] },
          flashcard: this.currentFlashcard,
        });
      }
    }, { immediate: true });
  },
  methods: {
    onAnswered() {
      if (!this.canSubmitAnswer) return;
      const correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
      this.correct = correct;
      this.answered = true;
    },
    onTag(flashcardStatusId) {
      if (!this.hasBeenAnswered || !this.currentFlashcardCategory) {
        this.goToNextFlashcard();
        return;
      }
      this.tagFlashcard(flashcardStatusId);
    },
    restoreStateFromFlashcardState(flashcard) {
      if (!flashcard) {
        return;
      }

      const categoryId = this.currentFlashcardCategory?.id ?? flashcard?.categoryIds?.[0];
      const stateId = `${flashcard?.id}-category-${categoryId}`;
      const state = this.$store.getters['flashcardStates/flashcardStates'][stateId];
      if (!state) {
        return;
      }

      this.setAnswerIds(state.answerIds);
      this.setRawAnswerData(state.rawAnswerData);
      this.correct = this.evaluateAnswer(flashcard, this.answerIds, this.rawAnswerData);
      this.answered = true;
    },
  },
};
</script>

<style lang='scss' scoped>
  #flashcard-view-container-v2 {
    @include flashcard-view-container-v2;
  }
</style>
